import { makeAutoObservable } from "mobx";
import { type IAnalyticsManager } from "services/analytics/analyticsManager";
import { type BillingClient } from "services/billing/billingClient";
import { PaymentStatus } from "services/billing/billingTypes";
import { type FormSubmitResult } from "types/form";

type BillingControllerState = "loading" | "error" | "success";

export class BillingControllerStore {
  private _state: BillingControllerState = "loading";
  private _isPaymentCompleted = false;
  private _isPaymentRequired = true;

  constructor(
    private readonly _billingClient: BillingClient,
    private readonly _analyticsManager: IAnalyticsManager,
  ) {
    makeAutoObservable(this);
  }

  get state(): BillingControllerState {
    return this._state;
  }

  set state(value: BillingControllerState) {
    this._state = value;
  }

  get isPaymentCompleted(): boolean {
    return this._isPaymentCompleted;
  }

  get isPaymentRequired(): boolean {
    return this._isPaymentRequired;
  }

  async charge(): Promise<FormSubmitResult> {
    const billingResult = await this._billingClient.charge();
    this._isPaymentRequired = billingResult.paymentRequired;
    // No payment required means nothing to charge — treat as success
    this.state = billingResult.isPaid || !billingResult.paymentRequired ? "success" : "error";
  }

  async fetchPaymentStatus(): Promise<void> {
    // getPaymentStatus() throws rather than swallowing failures (see billingClient.ts) — this is
    // just a gate for skipping the BASKET/CHARGE steps, so falling back to "not completed, payment
    // required" on a read failure is the safe default: worst case the customer sees the payment
    // step again instead of silently skipping it.
    let paymentStatus;
    try {
      paymentStatus = await this._billingClient.getPaymentStatus();
    } catch {
      this._analyticsManager.event("payment-status-fetch-failed");
      this._isPaymentCompleted = false;
      this._isPaymentRequired = true;
      return;
    }

    if (paymentStatus.status === undefined) {
      this._analyticsManager.event("payment-status-fetch-failed");
    }

    // A refunded (or refund-requested) payment means the payment obligation for this formation
    // attempt is settled - there's nothing left to charge, so treat it the same as PAID rather
    // than silently sending the customer back to pay again.
    this._isPaymentCompleted =
      paymentStatus.status === PaymentStatus.Paid ||
      paymentStatus.status === PaymentStatus.RefundRequested ||
      paymentStatus.status === PaymentStatus.Refunded;
    this._isPaymentRequired = paymentStatus.paymentRequired;
  }
}
